import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, ShieldCheck, Star, BadgeCheck, Globe, MessageSquare, Quote } from "lucide-react";
import { api } from "@/lib/api";
import { useApp } from "@/store";
import { Stars } from "@/components/Stars";

const STEP_ICONS = [Search, Star, MessageSquare];
const TRUST_ICONS = [ShieldCheck, BadgeCheck, Globe, MessageSquare];

const CONTENT = {
  en: {
    eyebrow: "How RentGlobe works",
    h1a: "Rent with confidence.", h1b: "Connect directly with advertisers.",
    hero: "RentGlobe is a trusted marketplace that connects people and businesses. You deal directly with advertisers — payments, deposits and bonds follow their own rules, so we stay out of the money to keep things simple.",
    browse: "Browse listings", list: "List your item",
    stepsHeading: "How it works in 3 simple steps", whyHeading: "Why it's safe", testiHeading: "What our community says",
    ctaHeading: "List and reach more customers", ctaBody: "Advertise your items or fleet, add your website and social links, and let renters contact you directly.", ctaBtn: "List your item now",
    steps: [
      { title: "List it", body: "Post what you're renting out: tools, equipment, vehicles, spaces, anything." },
      { title: "Get discovered", body: "Boost your listing to reach more renters, faster." },
      { title: "Connect directly", body: "Chat with interested renters and arrange the details yourselves." },
    ],
    trust: [
      { title: "A connector, not a middleman", body: "RentGlobe connects people and businesses. All payments, deposits and bonds happen off-platform under the advertiser's own terms — so you deal directly and transparently." },
      { title: "Verified advertisers (KYC)", body: "Advertisers can upload a government-issued ID that our team reviews for authenticity and a matching account name. The Verified ID badge means an ID document was checked — it does not verify the listing, the item or any rental agreement, so always use good judgement before you get in touch." },
      { title: "Businesses & individuals", body: "Car rental, real estate, construction and tool rental, event gear and individuals can all advertise here and follow their own rules." },
      { title: "Direct contact", body: "Reach advertisers through in-app chat or their own website, WhatsApp, Instagram, Facebook and more — whatever works best for you." },
    ],
  },
  pt: {
    eyebrow: "Como funciona o RentGlobe",
    h1a: "Alugue com confiança.", h1b: "Conecte-se diretamente com os anunciantes.",
    hero: "O RentGlobe é um marketplace confiável que conecta pessoas e empresas. Você negocia diretamente com os anunciantes — pagamentos, depósitos e cauções seguem as regras deles, então ficamos fora do dinheiro para manter tudo simples.",
    browse: "Ver anúncios", list: "Anunciar meu item",
    stepsHeading: "Como funciona em 3 passos simples", whyHeading: "Por que é seguro", testiHeading: "O que a comunidade diz",
    ctaHeading: "Anuncie e alcance mais clientes", ctaBody: "Divulgue seus itens ou sua frota, adicione seu site e redes sociais e deixe os locatários falarem diretamente com você.", ctaBtn: "Anunciar agora",
    steps: [
      { title: "Anuncie", body: "Publique o que você quer alugar: ferramentas, equipamentos, veículos, espaços, qualquer coisa." },
      { title: "Seja encontrado", body: "Impulsione seu anúncio para alcançar mais locatários, mais rápido." },
      { title: "Conecte-se diretamente", body: "Converse com os interessados e combinem os detalhes entre vocês." },
    ],
    trust: [
      { title: "Um conector, não um intermediário", body: "O RentGlobe conecta pessoas e empresas. Todos os pagamentos, depósitos e cauções acontecem fora da plataforma, sob os termos do próprio anunciante — então você negocia de forma direta e transparente." },
      { title: "Anunciantes verificados (KYC)", body: "Os anunciantes podem enviar um documento de identidade oficial que nossa equipe revisa quanto à autenticidade e ao nome correspondente à conta. O selo de identidade verificada significa que um documento foi analisado — ele não verifica o anúncio, o item nem qualquer acordo de aluguel; use sempre bom senso antes de entrar em contato." },
      { title: "Empresas e pessoas físicas", body: "Locadoras de carro, imobiliárias, aluguel de material de obra e ferramentas, equipamentos de eventos e pessoas físicas podem anunciar aqui e seguir suas próprias regras." },
      { title: "Contato direto", body: "Fale com os anunciantes pelo chat do app ou pelo site, WhatsApp, Instagram, Facebook e mais — o que for melhor para você." },
    ],
  },
  es: {
    eyebrow: "Cómo funciona RentGlobe",
    h1a: "Alquila con confianza.", h1b: "Conéctate directamente con los anunciantes.",
    hero: "RentGlobe es un marketplace confiable que conecta a personas y empresas. Tratas directamente con los anunciantes: los pagos, depósitos y fianzas siguen sus propias reglas, así que nos mantenemos fuera del dinero para que todo sea simple.",
    browse: "Ver anuncios", list: "Publicar mi artículo",
    stepsHeading: "Cómo funciona en 3 pasos simples", whyHeading: "Por qué es seguro", testiHeading: "Lo que dice nuestra comunidad",
    ctaHeading: "Publica y llega a más clientes", ctaBody: "Anuncia tus artículos o tu flota, agrega tu web y redes sociales, y deja que los inquilinos te contacten directamente.", ctaBtn: "Publicar ahora",
    steps: [
      { title: "Publícalo", body: "Publica lo que alquilas: herramientas, equipos, vehículos, espacios, lo que sea." },
      { title: "Hazte visible", body: "Impulsa tu anuncio para llegar a más inquilinos, más rápido." },
      { title: "Conecta directamente", body: "Chatea con los interesados y acuerden los detalles entre ustedes." },
    ],
    trust: [
      { title: "Un conector, no un intermediario", body: "RentGlobe conecta a personas y empresas. Todos los pagos, depósitos y fianzas ocurren fuera de la plataforma bajo los términos del anunciante, así que tratas de forma directa y transparente." },
      { title: "Anunciantes verificados (KYC)", body: "Los anunciantes pueden subir un documento de identidad oficial que nuestro equipo revisa en cuanto a autenticidad y coincidencia con el nombre de la cuenta. La insignia de identidad verificada significa que se revisó un documento — no verifica el anuncio, el artículo ni ningún acuerdo de alquiler; usa siempre buen juicio antes de contactar." },
      { title: "Empresas y particulares", body: "Alquiler de coches, inmobiliarias, alquiler de material de obra y herramientas, equipo para eventos y particulares pueden anunciar aquí con sus propias reglas." },
      { title: "Contacto directo", body: "Contacta a los anunciantes por el chat de la app o por su web, WhatsApp, Instagram, Facebook y más — lo que mejor te funcione." },
    ],
  },
  fr: {
    eyebrow: "Comment fonctionne RentGlobe",
    h1a: "Louez en toute confiance.", h1b: "Connectez-vous directement aux annonceurs.",
    hero: "RentGlobe est une place de marché de confiance qui met en relation particuliers et entreprises. Vous traitez directement avec les annonceurs — paiements, dépôts et cautions suivent leurs propres règles, nous restons donc en dehors de l'argent pour que tout reste simple.",
    browse: "Voir les annonces", list: "Publier mon article",
    stepsHeading: "Comment ça marche en 3 étapes simples", whyHeading: "Pourquoi c'est sûr", testiHeading: "Ce que dit notre communauté",
    ctaHeading: "Publiez et touchez plus de clients", ctaBody: "Annoncez vos articles ou votre flotte, ajoutez votre site et vos réseaux sociaux, et laissez les locataires vous contacter directement.", ctaBtn: "Publier maintenant",
    steps: [
      { title: "Publiez", body: "Mettez en ligne ce que vous louez : outils, équipements, véhicules, espaces, n'importe quoi." },
      { title: "Soyez visible", body: "Boostez votre annonce pour toucher plus de locataires, plus vite." },
      { title: "Connectez-vous directement", body: "Discutez avec les locataires intéressés et réglez les détails entre vous." },
    ],
    trust: [
      { title: "Un connecteur, pas un intermédiaire", body: "RentGlobe met en relation particuliers et entreprises. Tous les paiements, dépôts et cautions se font hors plateforme selon les conditions de l'annonceur — vous traitez donc de façon directe et transparente." },
      { title: "Annonceurs vérifiés (KYC)", body: "Les annonceurs peuvent téléverser une pièce d'identité officielle que notre équipe examine (authenticité et nom correspondant au compte). Le badge identité vérifiée signifie qu'un document a été contrôlé — il ne vérifie ni l'annonce, ni l'objet, ni un quelconque contrat de location ; faites toujours preuve de discernement avant de contacter." },
      { title: "Entreprises et particuliers", body: "Location de voitures, agences immobilières, location de matériel de chantier et d'outils, matériel événementiel et particuliers peuvent tous annoncer ici selon leurs propres règles." },
      { title: "Contact direct", body: "Joignez les annonceurs via le chat de l'app ou leur site, WhatsApp, Instagram, Facebook et plus — comme vous préférez." },
    ],
  },
};

const FALLBACK_TESTIMONIALS = [
  { name: "Marina S.", role: "renter", rating: 5, comment: "Found a camera near me and talked to the owner on WhatsApp — quick and easy." },
  { name: "Lucas P.", role: "owner", rating: 5, comment: "I advertise my drone here and arrange payment my own way. Great for reaching new customers." },
  { name: "Aisha K.", role: "renter", rating: 5, comment: "Loved that I could message the advertiser directly and agree the terms with them." },
];

export default function HowItWorks() {
  const { lang } = useApp();
  const c = CONTENT[lang] || CONTENT.en;
  const [testimonials, setTestimonials] = useState([]);
  useEffect(() => {
    api.get("/testimonials").then(({ data }) => setTestimonials(Array.isArray(data) && data.length ? data : FALLBACK_TESTIMONIALS))
      .catch(() => setTestimonials(FALLBACK_TESTIMONIALS));
  }, []);

  return (
    <div data-testid="how-it-works-page">
      <section className="bg-[#002FA7] px-5 py-20 text-white sm:px-8">
        <div className="mx-auto max-w-4xl">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-white/70">{c.eyebrow}</div>
          <h1 className="mt-3 font-display text-4xl font-extrabold leading-tight tracking-tight sm:text-5xl lg:text-6xl">{c.h1a}<br />{c.h1b}</h1>
          <p className="mt-5 max-w-2xl text-base text-white/80 sm:text-lg">{c.hero}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/" data-testid="hiw-browse" className="rounded-full bg-white px-6 py-3 text-sm font-bold text-[#002FA7] transition-transform hover:-translate-y-0.5">{c.browse}</Link>
            <Link to="/create" data-testid="hiw-list" className="rounded-full border border-white/40 px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-white/10">{c.list}</Link>
          </div>
        </div>
      </section>

      <section className="px-5 py-16 sm:px-8">
        <div className="mx-auto max-w-5xl">
          <h2 className="text-base font-bold uppercase tracking-[0.15em] text-gray-400 md:text-lg">{c.stepsHeading}</h2>
          <div className="mt-8 grid gap-5 sm:grid-cols-3">
            {c.steps.map((s, i) => {
              const Icon = STEP_ICONS[i];
              return (
                <div key={i} className="relative rounded-2xl border border-gray-200 p-6" data-testid={`hiw-step-${i}`}>
                  <span className="absolute right-5 top-5 font-display text-3xl font-extrabold text-gray-100">{i + 1}</span>
                  <span className="grid h-11 w-11 place-items-center rounded-xl bg-[#FF3B30]/10 text-[#FF3B30]"><Icon size={20} /></span>
                  <h3 className="mt-4 text-base font-bold text-gray-900">{s.title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-gray-500">{s.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-gray-50 px-5 py-16 sm:px-8">
        <div className="mx-auto max-w-5xl">
          <h2 className="text-base font-bold uppercase tracking-[0.15em] text-gray-400 md:text-lg">{c.whyHeading}</h2>
          <div className="mt-8 grid gap-5 sm:grid-cols-2">
            {c.trust.map((tr, i) => {
              const Icon = TRUST_ICONS[i];
              return (
                <div key={i} className="rounded-2xl border border-gray-200 bg-white p-6" data-testid={`hiw-card-${i}`}>
                  <span className="grid h-11 w-11 place-items-center rounded-xl bg-[#002FA7]/10 text-[#002FA7]"><Icon size={20} /></span>
                  <h3 className="mt-4 text-lg font-bold text-gray-900">{tr.title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-gray-500">{tr.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="px-5 py-16 sm:px-8" data-testid="testimonials">
        <div className="mx-auto max-w-5xl">
          <h2 className="text-base font-bold uppercase tracking-[0.15em] text-gray-400 md:text-lg">{c.testiHeading}</h2>
          <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {testimonials.map((tst, i) => (
              <div key={i} className="rounded-2xl border border-gray-200 bg-white p-6" data-testid={`testimonial-${i}`}>
                <Quote className="text-[#002FA7]/30" size={26} />
                <p className="mt-2 text-sm leading-relaxed text-gray-700">"{tst.comment}"</p>
                <div className="mt-4 flex items-center justify-between">
                  <div className="text-sm font-bold text-gray-900">{tst.name}<span className="ml-2 text-xs font-normal uppercase tracking-widest text-gray-400">{tst.role}</span></div>
                  <Stars value={tst.rating} size={14} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-5 py-16 sm:px-8">
        <div className="mx-auto max-w-5xl rounded-3xl bg-[#002FA7] p-10 text-center text-white">
          <Star className="mx-auto text-white/80" size={28} />
          <h2 className="mt-4 font-display text-2xl font-extrabold tracking-tight lg:text-3xl">{c.ctaHeading}</h2>
          <p className="mx-auto mt-3 max-w-xl text-white/80">{c.ctaBody}</p>
          <Link to="/create" data-testid="hiw-cta" className="mt-7 inline-block rounded-full bg-white px-7 py-3 text-sm font-bold text-[#002FA7] transition-transform hover:-translate-y-0.5">{c.ctaBtn}</Link>
        </div>
      </section>
    </div>
  );
}
